import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { TICKET_POLL_MS, ticketsApi } from '@/features/ai-dart/api/tickets';
import { TicketActions } from '@/features/ai-dart/components/tickets/TicketActions';
import { TicketBadge } from '@/features/ai-dart/components/tickets/TicketBadge';
import { TicketTimeline } from '@/features/ai-dart/components/tickets/TicketTimeline';
import { DartFormat } from '@/features/ai-dart/format';
import type { TicketDetail } from '@/features/ai-dart/types';

/** 섹션 제목 — 보드 상세(`DetailDrawer`)와 같은 표현을 쓴다. */
function SectionTitle({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <h2 className="mb-2 text-[11px] font-semibold tracking-wide text-muted uppercase">{children}</h2>;
}

/**
 * 속성 한 줄. 값이 비면 행 자체를 만들지 않는다 — "-"만 늘어선 표는 읽을 게 어디 있는지를 가린다.
 * 라벨·값 크기와 `leading-relaxed`·`break-words`는 `DetailDrawer`의 세부 정보 패널과 맞췄다.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element | null {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="grid grid-cols-[92px_1fr] items-start gap-2">
      <dt className="pt-0.5 text-[11.5px] text-muted">{label}</dt>
      <dd className="m-0 text-[12.5px] leading-relaxed break-words">{children}</dd>
    </div>
  );
}

export function TicketDetailView(): React.JSX.Element {
  const id = Number(useParams().id);
  const [search] = useSearchParams();
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(() => { if (Number.isSafeInteger(id)) ticketsApi.detail(id).then((value) => { setDetail(value); setError(''); }).catch((reason: Error) => setError(reason.message)); }, [id]);
  useEffect(() => { load(); const timer = window.setInterval(load, TICKET_POLL_MS); return () => window.clearInterval(timer); }, [load]);

  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-[12px] text-rose-700">{error}</div>;
  if (!detail) return <div className="p-8 text-center text-[12px] text-muted">티켓을 불러오는 중입니다.</div>;

  const { ticket } = detail;
  const backTo = `/ai-dart/tickets${search.size ? `?${search}` : ''}`;

  return (
    <div className="space-y-5">
      <Link className="inline-block text-[12px] text-accent transition hover:underline" to={backTo}>← 목록으로</Link>

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <TicketBadge value={ticket.status} />
          <TicketBadge value={ticket.severity} />
          {ticket.resolution && <span className="text-[11px] text-faint">{ticket.resolution}</span>}
          {ticket.recurrenceOf && <span className="text-[11px] text-faint">↻ #{ticket.recurrenceOf} 재발</span>}
        </div>
        <h1 className="text-[19px] leading-snug font-bold">
          <span className="text-muted">#{ticket.id}</span> {ticket.title ?? '(제목 없음)'}
        </h1>
        <p className="font-mono text-[11px] break-all text-faint">{ticket.fingerprint}</p>
      </header>

      <TicketActions ticket={ticket} onChanged={load} />

      {/*
        읽는 것(증거·타임라인)과 참조하는 것(속성·fingerprint)을 좌우로 가른다 — 증거를 읽으면서
        레포·발생 시각을 곁눈으로 확인하는 흐름이라 위아래로 늘어놓으면 스크롤을 왕복해야 한다.
        좁은 화면에서는 자연히 한 줄기로 접힌다.
      */}
      <section className="grid gap-4 lg:grid-cols-[1.7fr_1fr] lg:items-start">
        <div className="min-w-0 space-y-4">
          <article className="rounded-lg border border-line bg-surface p-4">
            <SectionTitle>증거 (redacted)</SectionTitle>
            <pre className="max-h-[560px] overflow-auto rounded-md bg-line-soft p-3 text-[11.5px] leading-relaxed whitespace-pre-wrap">
              {ticket.evidenceRedacted ?? '(없음)'}
            </pre>
          </article>

          {/*
            슬랙 제보에 붙어 온 화면 캡처. 증거 바로 아래에 둔다 — 텍스트만으로는 무엇이 잘못
            보였는지 알 수 없어 제보자가 캡처를 붙이고, 그걸 보려고 슬랙을 따로 열게 하지 않는다.
          */}
          {detail.images.length > 0 && (
            <article className="rounded-lg border border-line bg-surface p-4">
              <SectionTitle>첨부 이미지 ({detail.images.length})</SectionTitle>
              <div className="grid gap-3 sm:grid-cols-2">
                {detail.images.map((image, index) => (
                  <figure key={`${index}-${image.name}`} className="m-0 space-y-1">
                    <a href={ticketsApi.imageSrc(ticket.id, index)} target="_blank" rel="noreferrer">
                      <img
                        className="w-full rounded-md border border-line bg-line-soft"
                        src={ticketsApi.imageSrc(ticket.id, index)}
                        alt={image.name}
                        loading="lazy"
                      />
                    </a>
                    <figcaption className="truncate text-[11px] text-faint">{image.name}</figcaption>
                  </figure>
                ))}
              </div>
            </article>
          )}

          <article className="rounded-lg border border-line bg-surface p-4">
            <SectionTitle>이벤트 타임라인 ({detail.events.length})</SectionTitle>
            <TicketTimeline events={detail.events} />
          </article>
        </div>

        <aside className="min-w-0 space-y-4">
          <article className="rounded-lg border border-line bg-surface p-4">
            <SectionTitle>속성</SectionTitle>
            {/* 사이드바에서는 한 줄에 한 항목 — 긴 값은 `break-words`로 흘리고 줄여 끊지 않는다. */}
            <dl className="flex flex-col gap-2.5">
              <Field label="레포">{ticket.repo ?? <span className="text-faint">귀속 미상</span>}</Field>
              <Field label="소스">{ticket.source}</Field>
              <Field label="발생 횟수">{ticket.occurrenceCount}</Field>
              <Field label="최초 발생">{DartFormat.kst(ticket.firstSeenAt)}</Field>
              <Field label="최근 발생">{DartFormat.kst(ticket.lastSeenAt)}</Field>
              <Field label="workdir">{ticket.workdir && <span className="font-mono text-[11.5px]">{ticket.workdir}</span>}</Field>
              <Field label="PR">
                {ticket.prUrl && (
                  <a className="text-accent transition hover:underline" href={ticket.prUrl} target="_blank" rel="noreferrer">{ticket.prUrl}</a>
                )}
              </Field>
              <Field label="원본">
                {ticket.sourceUrl && (
                  <a className="text-accent transition hover:underline" href={ticket.sourceUrl} target="_blank" rel="noreferrer">원본 열기 ↗</a>
                )}
              </Field>
            </dl>
            {ticket.noiseReason && (
              <p className="mt-3 border-t border-line pt-3 text-[12px] leading-relaxed text-muted">
                <strong className="text-[11.5px]">noise 근거</strong> · {ticket.noiseReason}
              </p>
            )}
          </article>

          {/* 병합이 실제로 일어났을 때만 보여준다 — 1건짜리 목록은 화면만 차지한다. */}
          {detail.fingerprints.length > 1 && (
            <article className="rounded-lg border border-line bg-surface p-4">
              <SectionTitle>병합 fingerprint ({detail.fingerprints.length})</SectionTitle>
              <ul className="space-y-1 font-mono text-[11px] text-muted">
                {detail.fingerprints.map((fingerprint) => <li key={fingerprint} className="break-all">{fingerprint}</li>)}
              </ul>
            </article>
          )}
        </aside>
      </section>
    </div>
  );
}
